import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { CreditCardValidators } from '../../../../../shared/creditcardvalidators';
import { CreditCardFormatDirective } from '../../../../../directives/credit-card-format.directive';
import { CardType } from 'src/app/models/payment.model';
import { AddCreditCardValidators } from 'src/app/directives/credit-card.directive';
import { ChildEvent, LabelValuePair, ScrBaseComponent } from 'src/app/components/base.component';
import { ConfirmationService } from 'primeng/api';
import { NgxMaskDirective } from 'ngx-mask';
import { HttpErrorResponse } from '@angular/common/http';
import { Address, PaymentMethodsService, ConfigService, Country, Region } from '../../../../../services';
import { MaxLen } from '../../../../../shared/constants';
import { FirstNameValidatorDirective, LastNameValidatorDirective } from 'src/app/directives/validators.directive';


@Component({
  selector: 'app-add-card',
  templateUrl: './add-card.component.html',
  styleUrls: ['./add-card.component.scss']
})
export class AddCardComponent extends ScrBaseComponent implements OnInit {

  creditCardForm!: UntypedFormGroup;
  countries: Country[] = [];
  cards!: CardType[];
  cardTypeValidator?: string;
  months!: LabelValuePair[];
  years: LabelValuePair[] = [];
  submitted = false;
  @Input() hasDefault!: boolean;
  // public regionList: Map<string, Region[]> = new Map<string, Region[]>();
  public regionList: Region[] = [];
  public errorMap = new Map<string, string>();
  @ViewChild('ccNumber', { static: false }) ccNumber?: CreditCardFormatDirective;

  @Output() emitAction = new EventEmitter<ChildEvent>();
  dynamicMask!: string;
  @ViewChild(NgxMaskDirective) maskDir: NgxMaskDirective | undefined;


  constructor(private configService: ConfigService,
    private formBuilder: UntypedFormBuilder,
    private paymentMethodService: PaymentMethodsService,
    private confirmService: ConfirmationService) {
    super();
    this.createFormControls();
  }

  ngOnInit(): void {

    this.configService.getCountries().subscribe(countries => {
      this.countries = countries;
      this.creditCardForm.patchValue({ country: this.countries.find(({ country }) => country === 'us')?.country });
      this.getRegions(this.creditCardForm.get('country')?.value);
    }, (error: HttpErrorResponse) => { this.processHttpError(error); this.jumpToTop(); });

  }

  getRegions(value: string | null, resetZipState?: boolean) {

    if (value) {
      this.configService.getRegions(value).subscribe(regions => {
        this.regionList = regions as Region[];
      });
    }
    if (value === 'ca') {
      this.dynamicMask = 'S0S 0S0';
    } else {
      this.dynamicMask = '';
    }
    if (resetZipState) {
      this.creditCardForm.get('zip')?.reset();
      this.creditCardForm.get('state')?.reset();
    }
  }

  createFormControls() {
    const currentDate = new Date();
    // console.log(currentDate.getFullYear());
    this.cards = [
      { name: "Visa", code: "Visa" },
      { name: "Mastercard", code: "Mastercard" },
      { name: "Discover", code: "Discover" },
      { name: "American Express", code: "Amex" }
    ];

    this.months = [
      { label: "01", value: 1 },
      { label: "02", value: 2 },
      { label: "03", value: 3 },
      { label: "04", value: 4 },
      { label: "05", value: 5 },
      { label: "06", value: 6 },
      { label: "07", value: 7 },
      { label: "08", value: 8 },
      { label: "09", value: 9 },
      { label: "10", value: 10 },
      { label: "11", value: 11 },
      { label: "12", value: 12 }
    ];

    for (let x = 0; x < 12; x++) {
      this.years.push({ label: (currentDate.getFullYear() + x).toString(), value: currentDate.getFullYear() + x });
    }
    // console.log(this.years);
    this.creditCardForm = this.formBuilder.group(
      {
        firstName: ['', [Validators.required, Validators.maxLength(MaxLen.FirstName), AddCreditCardValidators.WhiteSpaceValidation(), new FirstNameValidatorDirective().fct()]],
        lastName: ['', [Validators.required, Validators.maxLength(MaxLen.LastName), AddCreditCardValidators.WhiteSpaceValidation(), new LastNameValidatorDirective().fct()]],
        country: ['', [Validators.required]],
        address1: ['', [Validators.required, Validators.maxLength(MaxLen.Address), AddCreditCardValidators.WhiteSpaceValidation()]],
        address2: ['', [Validators.maxLength(MaxLen.Address), AddCreditCardValidators.WhiteSpaceValidation()]],
        city: ['', [Validators.required, Validators.maxLength(MaxLen.City), AddCreditCardValidators.WhiteSpaceValidation()]],
        state: [''],
        zip: ['', AddCreditCardValidators.WhiteSpaceValidation()],
        description: ['', [Validators.maxLength(30)]],
        cardType: ['', [Validators.required, Validators.maxLength(32)]],
        creditCardNumber: ['', [Validators.required, CreditCardValidators.validateCCNumber]],
        expirationMonth: ['', [Validators.required]],
        expirationYear: ['', [Validators.required]],
        cvv: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(4)]],
        default: [false],
        cardTypeLib: [null]
      }, {
      validators: [AddCreditCardValidators.CheckCreditCardTypeToNum(), AddCreditCardValidators.CheckExpirationDate(),
      AddCreditCardValidators.CustomRequired(), AddCreditCardValidators.CustomCVV(), AddCreditCardValidators.CustomZipInvalid()],
      updateOn: 'blur'
    });

    this.errorMap.set('Visa', 'A 13 or 16 digit credit card number is required.');
    this.errorMap.set('Mastercard', 'A 16 digit credit card number is required.');
    this.errorMap.set('Discover', 'A 16 digit credit card number is required.');
    this.errorMap.set('Amex', 'A 15 digit credit card number is required.');

  }

  submit() {
    this.clearMessages();
    this.submitted = true;

    if (!this.creditCardForm.valid) {
      this.scrollToFirstError();
      return;
    }

    const prunedData = this.removeEmpty(this.creditCardForm.value);
    const formData = JSON.parse(JSON.stringify(prunedData), this.prepareForSubmit);
    delete formData['cardTypeLib'];

    this.observe(this.paymentMethodService.addCreditCard(formData), res => {
      if (res.messages && res.messages.length > 0) {
        this.emitAction.emit({ action: 'reload', value: res.cc?.creditCardId, message: res.messages[0].message });
      }
      else {
        this.emitAction.emit({ action: 'reload', value: res.cc?.creditCardId, messageCode: 'scr.cc.updated' });
      }
      return false;
    });
  }

  removeEmpty(obj: Record<string,unknown> | ArrayLike<unknown>) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v != ''));
  }

  prepareForSubmit(key: string, value: string | number | boolean) {

    if (typeof value === 'string') {
      if (key === 'creditCardNumber') {
        return value.split(' ').join('');
      }
      return value.trim();
    }
    return value;
  }

  setCreditcardDate() {
    if (this.creditCardForm.controls.expirationMonth.value && this.creditCardForm.controls.expirationYear.value) {
      // console.log(this.creditCardForm);
      // this.creditCardForm.controls.expirationDate.setValue()
    }
  }

  // we are faking a value on the form to compare the card type the user entered vs the type from the ang cc lib 
  cardTypeFromNum() {
    this.ccNumber?.resolvedScheme$.subscribe(res => {
      this.cardTypeValidator = res;
      this.creditCardForm.patchValue({ cardTypeLib: res });
    });
  }

  getCardError(cardType: string, cardNum: string): string | undefined {

    if (this.errorMap.has(cardType) && cardType === 'Visa' && (cardNum.replace(/\s/g, '').length != 13 && cardNum.replace(/\s/g, '').length != 16)) {
      return this.errorMap.get(cardType);
    }
    if (this.errorMap.has(cardType) && cardType === 'Discover' && (cardNum.replace(/\s/g, '').length != 16)) {
      return this.errorMap.get(cardType);
    }
    if (this.errorMap.has(cardType) && cardType === 'Mastercard' && (cardNum.replace(/\s/g, '').length != 16)) {
      return this.errorMap.get(cardType);
    }
    if (this.errorMap.has(cardType) && cardType === 'Amex' && (cardNum.replace(/\s/g, '').length != 15)) {
      return this.errorMap.get(cardType);
    }
    return 'The value in the Card Number is invalid.';

  }

  confirmCancelDialog() {
    this.confirmService.confirm({
      key: 'confirmCancel',
      header: 'Confirm Cancellation',
      message: 'Are you sure you want to cancel the request?',
      accept: () => {
        this.creditCardForm.reset();
        this.submitted = false;
        this.emitAction.emit({ action: 'cancel', value: 'cancel' });
      },
      reject: () => {
        this.confirmService.close();
      }
    });
  }

  limitCcLength(event: Event) {
    const target = event.target as HTMLInputElement;
    const match = target.value.match(/(\D*\d\D*){15}\d/);

    if (match)
      this.creditCardForm.get('creditCardNumber')?.setValue(match[0]);
  }

  loadAddress(address: Address) {
    this.creditCardForm.get('firstName')?.setValue(address.firstName);
    this.creditCardForm.get('lastName')?.setValue(address.lastName);
    this.creditCardForm.get('address1')?.setValue(address.address1);
    this.creditCardForm.get('address2')?.setValue(address.address2);
    this.creditCardForm.get('city')?.setValue(address.city);
    this.creditCardForm.get('state')?.setValue(address.state);
    this.creditCardForm.get('zip')?.setValue(address.zip);
    this.creditCardForm.get('country')?.setValue(address.country);
  }

}
